<template>
  <section class="v2-price-source">
    <h4 class="v2-price-source__heading">
      Prices
    </h4>
    <p class="v2-price-source__hint">
      Public link only. Buyers see prices from this source, in its currency.
      A product it has no price for shows “Price not available”.
    </p>

    <label class="v2-price-source__option">
      <input
        type="radio"
        name="price-source"
        :value="modes.BASE"
        :checked="selection.mode === modes.BASE"
        :disabled="loading"
        @change="$emit('change', { mode: modes.BASE })"
      >
      <span>Base price{{ options.baseCurrency ? ` (${options.baseCurrency})` : '' }}</span>
    </label>

    <label class="v2-price-source__option">
      <input
        type="radio"
        name="price-source"
        :value="modes.PRICE_BOOK"
        :checked="selection.mode === modes.PRICE_BOOK"
        :disabled="loading || !priceBookOptions.length"
        @change="$emit('change', { mode: modes.PRICE_BOOK })"
      >
      <span>
        Price book
        <em v-if="!priceBookOptions.length">No price books with a currency for this collection</em>
      </span>
    </label>
    <v2-select
      v-if="selection.mode === modes.PRICE_BOOK"
      class="v2-price-source__select"
      :value="selection.priceBookId"
      :options="priceBookOptions"
      :reduce="option => option.value"
      :clearable="false"
      placeholder="Choose a price book"
      @input="priceBookId => $emit('change', { priceBookId })"
    />

    <label class="v2-price-source__option">
      <input
        type="radio"
        name="price-source"
        :value="modes.CURRENCY_BOOK"
        :checked="selection.mode === modes.CURRENCY_BOOK"
        :disabled="loading || !options.currencies.length"
        @change="$emit('change', { mode: modes.CURRENCY_BOOK })"
      >
      <span>
        Currency book
        <em v-if="!options.currencies.length">No currency book prices uploaded for this collection</em>
      </span>
    </label>
    <v2-select
      v-if="selection.mode === modes.CURRENCY_BOOK"
      class="v2-price-source__select"
      :value="selection.currency"
      :options="options.currencies"
      :clearable="false"
      placeholder="Choose a currency"
      @input="currency => $emit('change', { currency })"
    />

    <div
      v-if="missingProducts.length"
      class="v2-price-source__missing"
      role="status"
    >
      <strong>
        {{ missingProducts.length }} {{ missingProducts.length === 1 ? 'product has' : 'products have' }}
        no price in {{ sourceLabel }} and will show without a price
      </strong>
      <ul>
        <li
          v-for="product in missingProducts"
          :key="product.productId"
        >
          {{ product.name || product.productId }}<span v-if="product.styleCode"> · {{ product.styleCode }}</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import V2Select from '@/components/v2/V2Select.vue'
import { PRICE_SOURCE_MODES } from '@/composables/useWishlistPriceSource'

/** The "Prices" picker of the sharing dialog (KP-4586). State lives in the parent. */
export default {
  name: 'V2WishlistPriceSourceSection',
  components: { V2Select },
  props: {
    selection: { type: Object, required: true },
    options: { type: Object, required: true },
    missingProducts: { type: Array, default: () => [] },
    sourceLabel: { type: String, default: '' },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { modes: PRICE_SOURCE_MODES }
  },
  computed: {
    // The book's own currency, never a guess from its name.
    priceBookOptions() {
      return this.options.priceBooks.map(book => ({
        label: `${book.priceBookName} (${book.currency})`,
        value: book._id,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-price-source {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__heading {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  &__hint {
    margin: 0;
    font-size: 0.8rem;
    color: var(--v2-muted-foreground);
  }

  &__option {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    cursor: pointer;

    em {
      display: block;
      font-style: normal;
      font-size: 0.8rem;
      color: var(--v2-muted-foreground);
    }
  }

  &__select {
    margin-left: 1.6rem;
  }

  &__missing {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--v2-border, rgba(0, 0, 0, 0.1));
    border-radius: var(--v2-radius-md, 6px);

    ul {
      margin: 0.25rem 0 0;
      padding-left: 1rem;
      max-height: 140px;
      overflow-y: auto;
    }
  }
}
</style>
